import React, { createRef, forwardRef } from 'react';
import { Form } from 'ALIASED-antd';
import { Input, InputRef } from 'antd';
import { debounce } from 'lodash';
import { SearchOutlined } from 'antd/icons';
import formatDate from 'vl-common/src/lib/formatDate';
import styled from '@emotion/styled';
import UnstyledButton from './UnstyledButton';
import i18n from 'vl-common/src/lib/i18n';
import { FilterDropdownProps } from 'ALIASED-antd/lib/table/interface';

const t = i18n.getFixedT(null, 'main');

const queryChanged = debounce((setSelectedKeys, value, dataIndex, setQuery, confirm) => {
  if (value === 'Invalid date') {
    value = '';
  }
  setSelectedKeys(value ? [value] : []);
  setQuery((query) => ({ ...query, [dataIndex]: value }));
  if (dataIndex !== 'date_start') {
    confirm();
  }
}, 1000);

const dateChanged = (setSelectedKeys, value, dataIndex, setQuery, confirm) => {
  if (value) {
    setSelectedKeys(value ? [value] : []);
    setQuery((query) => ({ ...query, [dataIndex]: formatDate(new Date(value), 'Date only (ISO) - 2022-03-11') }));
  } else {
    setQuery((query) => {
      delete query[dataIndex];
      return { ...query };
    });
    setSelectedKeys([]);
  }
  if (dataIndex !== 'date_start') {
    confirm();
  }
};

const DateRangeFieldEnum = {
  date_start: 'date_start',
  date_end: 'date_end'
};
type DateRangeFields = Record<keyof typeof DateRangeFieldEnum, string>;

const DateRangeFormItem = styled(Form.Item)`
  margin-bottom: 4px;
`;

const checkDateValidity = (date: string) => {
  if (date === '') return true;

  try {
    formatDate(new Date(date), 'Date only (ISO) - 2022-03-11');
    return true;
  } catch {
    return false;
  }
};

const checkDateChronology = (earlierDate: string, laterDate: string) => {
  const earlierDateObj = new Date(earlierDate);
  const laterDateObj = new Date(laterDate);
  return earlierDateObj.getTime() <= laterDateObj.getTime();
};

const DateRange = forwardRef<InputRef, { setStartDate: (date: string) => void; setEndDate: (date: string) => void }>(
  ({ setStartDate, setEndDate }, startDateInputReference) => {
    const [form] = Form.useForm<DateRangeFields>();
    const endDateInputReference = createRef<InputRef>();

    const setFieldErrors = (fromError: string[] | undefined, toError: string[] | undefined) => {
      form.setFields([
        {
          name: DateRangeFieldEnum.date_start,
          errors: fromError
        },
        {
          name: DateRangeFieldEnum.date_end,
          errors: toError
        }
      ]);
    };

    const changeHandler = debounce(() => {
      const from = form.getFieldValue(DateRangeFieldEnum.date_start);
      const to = form.getFieldValue(DateRangeFieldEnum.date_end);

      if (!checkDateValidity(from) && !checkDateValidity(to)) return;

      if (from && to && !checkDateChronology(from, to)) {
        setFieldErrors([''], ['\u2018To\u2019 must be later than \u2018From\u2019']);
        return;
      }

      setFieldErrors(undefined, undefined);

      setStartDate(from);
      setEndDate(to);
    }, 1000);

    const onStartDateInputKeyDownHandler = (e) => {
      // When the user navigates through the application using the tab button,
      // it will remove the focus from the Start Date input, which is focused by default
      // and will move the focus to the End Date input
      if (e.key === 'Tab' && !e.shiftKey) {
        e.target.blur();
        setTimeout(() => {
          endDateInputReference.current.focus();
        }, 100);
      }
    };

    const onEndDateInputKeyDownHandler = (e) => {
      // When the user navigates through the application using the shift + tab buttons,
      // it will remove the focus from the End Date input
      // and will move the focus to the Start Date input
      if (e.target.id === 'date_end' && e.key === 'Tab' && e.shiftKey) {
        e.stopPropagation();
        e.target.blur();
        setTimeout(() => {
          startDateInputReference.current.focus();
        }, 100);
      }
    };

    return (
      <Form form={form} css={{ padding: 4 }} labelCol={{ span: 6 }}>
        <DateRangeFormItem label={t('From')} name={DateRangeFieldEnum.date_start}>
          <Input
            type="date"
            ref={startDateInputReference}
            onChange={changeHandler}
            onKeyDown={onStartDateInputKeyDownHandler}
          />
        </DateRangeFormItem>
        <DateRangeFormItem label={t('To')} name={DateRangeFieldEnum.date_end}>
          <Input
            type="date"
            ref={endDateInputReference}
            onChange={changeHandler}
            onKeyDown={onEndDateInputKeyDownHandler}
          />
        </DateRangeFormItem>
      </Form>
    );
  }
);

export const filterColumn = (placeholder, dataIndex, setQuery, query, exact, dateFilter) => {
  const inputReference = createRef<InputRef>();
  return {
    filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => {
      if (dateFilter) {
        return (
          <DateRange
            setStartDate={(date) => dateChanged(setSelectedKeys, date, 'date_start', setQuery, confirm)}
            setEndDate={(date) => dateChanged(setSelectedKeys, date, 'date_end', setQuery, confirm)}
            ref={inputReference}
          />
        );
      }

      return (
        <Input
          placeholder={t('Search {{placeholder}}', { placeholder })}
          onBlur={(e) => {
            if (exact) {
              queryChanged(setSelectedKeys, e.target.value, dataIndex, setQuery, confirm);
            }
          }}
          onChange={(e) => {
            if (!exact) {
              queryChanged(setSelectedKeys, e.target.value, dataIndex, setQuery, confirm);
            }
          }}
          ref={inputReference}
        />
      );
    },
    filterIcon: (filtered: boolean) => (
      <UnstyledButton data-testid="filter-button">
        <SearchOutlined css={filtered && { color: '#1890ff' }} />
      </UnstyledButton>
    ),
    filterDropdownProps: {
      onOpenChange: (open: boolean) => (open ? setTimeout(() => inputReference.current?.focus(), 100) : undefined)
    }
  };
};

export default filterColumn;
